import React from "react";
import styled from "styled-components/macro";
import { theme } from "ui/theme";

import Button from "ui/Button";
import GoogleLogo from "images/google-logo.svg";
import MicrosoftLogo from "images/ms-logo-sign-in.svg";

// This button needs a white background to comply with
// Google and Microsoft logo requirements.
const buttonBase = `
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 500;
  background-color: ${theme.ColorWhite} !important;

  &:hover,
  &:focus,
  &.disabled {
    background-color: ${theme.ColorWhite} !important;
  }
`;

const defaultWidth = `18.5rem`;

const $GoogleLoginBtn = styled(Button)`
  ${buttonBase}
  ${(props) => !props.block && `width: ${defaultWidth};`}
  img {
    max-width: 1.5rem;
    margin-right: 1rem;
  }
`;

const $MicrosoftLoginBtn = styled(Button)`
  ${buttonBase}
  ${(props) => !props.block && `width: ${defaultWidth};`}
`;

// NOTE: width cannot be rem here, it should not scale.
const $MicrosoftSignInLogoWrapper = styled.span`
  display: inline-block;
  width: 33px;
  overflow: hidden;
`;

const $MicrosoftSignInLogo = styled.img`
  width: 11.35rem;
`;

type OAuthService = "Google" | "Microsoft";

function getOAuthURL(
  type: OAuthService,
  isSignup: boolean,
  isExpeditedOnboarding: boolean,
) {
  const service = type === "Google" ? "google_oauth2" : "azure_oauth2";
  const href = `/auth/${service}`;
  if (isExpeditedOnboarding) {
    return `${href}?intent=expedited_educator_onboarding`;
  }
  if (isSignup) {
    return `${href}?intent=educator_signup`;
  }
  return href;
}

interface OAuthButtonVariationProps {
  href: string;
  disabled?: boolean;
  block?: boolean;
  isSignup?: boolean;
}

function OAuthGoogleButton(props: OAuthButtonVariationProps) {
  const textInButton = `Sign ${props.isSignup ? "up" : "in"} with Google`;

  return (
    <$GoogleLoginBtn
      block={props.block}
      href={props.href}
      disabled={props.disabled}
      variant="secondary"
      data-testid="google-login"
      analyticsName="clicked_google_login"
      requestInProgress={false}
    >
      <img src={GoogleLogo} alt="Google Logo" />
      {textInButton}
    </$GoogleLoginBtn>
  );
}

function OAuthMicrosoftButton(props: OAuthButtonVariationProps) {
  const textInButton = `Sign ${props.isSignup ? "up" : "in"} with Microsoft`;
  return (
    <$MicrosoftLoginBtn
      block={props.block}
      href={props.href}
      disabled={props.disabled}
      variant="secondary"
      analyticsName="clicked_microsoft_login"
      requestInProgress={false}
    >
      <$MicrosoftSignInLogoWrapper>
        <$MicrosoftSignInLogo src={MicrosoftLogo} alt="Microsoft Logo" />
      </$MicrosoftSignInLogoWrapper>
      {textInButton}
    </$MicrosoftLoginBtn>
  );
}

export interface OAuthButtonProps {
  type: OAuthService;
  isSignup?: boolean;
  disabled?: boolean;
  block?: boolean;
  isExpeditedOnboarding?: boolean;
}
export default function OAuthButton(props: OAuthButtonProps) {
  const href = getOAuthURL(
    props.type,
    !!props.isSignup,
    !!props.isExpeditedOnboarding,
  );

  return props.type === "Google" ? (
    <OAuthGoogleButton href={href} {...props} />
  ) : (
    <OAuthMicrosoftButton href={href} {...props} />
  );
}
